/**
 * MELANUA Cart Context
 * Global cart state management for the e-commerce experience
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi } from '../utils/api';

const CartContext = createContext(null);

export function CartProvider({ children }) {
    const [cart, setCart] = useState({ items: [], item_count: 0, subtotal: '0.00' });
    const [isLoading, setIsLoading] = useState(true);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [notification, setNotification] = useState(null);

    // Fetch cart on mount
    const fetchCart = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await cartApi.get();
            // Ensure items is always an array
            setCart({
                ...data,
                items: data.items || [],
            });
        } catch (error) {
            console.error('Failed to fetch cart:', error);
            // Reset to empty cart on error
            setCart({ items: [], item_count: 0, subtotal: '0.00' });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    // Show notification
    const showNotification = useCallback((message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    }, []);

    // Add item to cart
    const addItem = useCallback(async (productId, variantId = null, quantity = 1) => {
        try {
            const data = await cartApi.addItem(productId, variantId, quantity);
            setCart({ ...data, items: data.items || [] });
            showNotification('Added to cart');
            setIsCartOpen(true); // Open cart drawer
            return { success: true };
        } catch (error) {
            showNotification(error.message || 'Failed to add item', 'error');
            return { success: false, error: error.message };
        }
    }, [showNotification]);

    // Update item quantity
    const updateItem = useCallback(async (itemId, quantity) => {
        try {
            const data = await cartApi.updateItem(itemId, quantity);
            setCart(data);
            return { success: true };
        } catch (error) {
            showNotification(error.message, 'error');
            return { success: false, error: error.message };
        }
    }, [showNotification]);

    // Remove item from cart
    const removeItem = useCallback(async (itemId) => {
        try {
            const data = await cartApi.removeItem(itemId);
            setCart(data);
            showNotification('Item removed');
            return { success: true };
        } catch (error) {
            showNotification(error.message, 'error');
            return { success: false, error: error.message };
        }
    }, [showNotification]);

    // Clear cart
    const clearCart = useCallback(async () => {
        try {
            const data = await cartApi.clear();
            setCart(data);
            return { success: true };
        } catch (error) {
            showNotification(error.message, 'error');
            return { success: false, error: error.message };
        }
    }, [showNotification]);

    // Toggle cart drawer
    const toggleCart = useCallback(() => {
        setIsCartOpen(prev => !prev);
    }, []);

    // Open cart drawer
    const openCart = useCallback(() => {
        setIsCartOpen(true);
    }, []);

    // Close cart drawer
    const closeCart = useCallback(() => {
        setIsCartOpen(false);
    }, []);

    const value = {
        cart,
        isLoading,
        isCartOpen,
        notification,
        addItem,
        updateItem,
        removeItem,
        clearCart,
        toggleCart,
        openCart,
        closeCart,
        refreshCart: fetchCart,
        itemCount: cart.item_count || 0,
        subtotal: cart.subtotal || '0.00',
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}

export default CartContext;
